import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiDownload,
  FiLayers,
  FiUpload,
} from 'react-icons/fi';
import { GiSprout } from 'react-icons/gi';
import api from '../../../utils/api';
import { mapPrintService } from '../services/mapPrintService';
import { assetsService } from '../../../services/assetsService';
import { YPageShell, YPageShellBody } from '../../../ui/shell/YPageShell';
import { YCard } from '../../../ui/YCard';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { useToast } from '../../../components/ui/ToastProvider';
import { ServerMapPrintPreview } from '../components/ServerMapPrintPreview';
import { PrintableMapFrozenPreview } from '../components/PrintableMapFrozenPreview';
import { PrintableMapPrintGuidance } from '../components/PrintableMapPrintGuidance';
import { PrintableMapStepper } from '../components/PrintableMapStepper';
import { PrintableMapOrderSummary } from '../components/PrintableMapOrderSummary';
import { ToolkitShellTabs } from '../components/ToolkitShellTabs';
import {
  BOUNDARY_LINE_SCALE_MAX,
  BOUNDARY_LINE_SCALE_MIN,
  DEFAULT_BOUNDARY_LINE_SCALE,
  DEFAULT_LAYERS,
  DEFAULT_LOGO_POSITION,
  DEFAULT_ORIENTATION,
  DEFAULT_OUTLINE_COLOR,
  DEFAULT_SIZE,
  LAYER_DEFS,
  LOGO_POSITIONS,
  ORIENTATIONS,
  OUTLINE_SWATCHES,
  PREVIEW_FRAME_HEIGHT,
  SIZES,
  WIZARD_STEPS,
  canContinueStep,
  formatCoords,
  getSizeOption,
} from './printableMapConstants';
import { geojsonToSvgPolygonPoints, getGeojsonCentroid } from '../utils/printableMapGeo';
import { clubTracts } from '../../map/utils/clubBoundaryRole';
import { loadLogoChoice, saveLogoChoice } from '../utils/mapPrintLogoStorage';
import { errorDetailFromResponse } from '../../../utils/downloadBlob';
import './printable-map-shell.css';

const LAYER_ICONS = {
  stands: FiLayers,
  plots: GiSprout,
};

function StepHeading({ stepCode, title, subtitle }) {
  return (
    <div className="pm-step-heading">
      <div className="pm-mono-label pm-mono-label--accent">{stepCode}</div>
      <h2>{title}</h2>
      {subtitle ? <p>{subtitle}</p> : null}
    </div>
  );
}

function BoundaryThumbnail({ geojson, patternId }) {
  const points = geojsonToSvgPolygonPoints(geojson);
  return (
    <svg viewBox="0 0 100 80" width="100%" height="100%" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <pattern id={patternId} width="10" height="10" patternUnits="userSpaceOnUse">
          <path d="M10 0H0V10" fill="none" stroke="var(--color-border)" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="100" height="80" fill={`url(#${patternId})`} />
      {points ? (
        <polygon
          points={points}
          fill="color-mix(in srgb, var(--color-brand-primary) 22%, transparent)"
          stroke="var(--color-brand-primary)"
          strokeWidth="1.6"
        />
      ) : null}
    </svg>
  );
}

export function PrintableMapPage() {
  const { show: showToast } = useToast();
  const [step, setStep] = useState(1);
  const [boundaries, setBoundaries] = useState([]);
  const [loadingBoundaries, setLoadingBoundaries] = useState(true);
  const [boundariesError, setBoundariesError] = useState('');
  const [huntBoundaryId, setHuntBoundaryId] = useState('');
  const [title, setTitle] = useState('');
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [orientation, setOrientation] = useState(DEFAULT_ORIENTATION);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoPosition, setLogoPosition] = useState(DEFAULT_LOGO_POSITION);
  const [boundaryFillOpacity, setBoundaryFillOpacity] = useState(0.18);
  const [boundaryLineScale, setBoundaryLineScale] = useState(DEFAULT_BOUNDARY_LINE_SCALE);
  const [boundaryLineColor, setBoundaryLineColor] = useState(DEFAULT_OUTLINE_COLOR);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewSnapshot, setPreviewSnapshot] = useState(null);

  const style = 'satellite';
  const layers = DEFAULT_LAYERS;

  const selectedBoundary = useMemo(
    () => boundaries.find((b) => String(b.id) === huntBoundaryId) || null,
    [boundaries, huntBoundaryId]
  );

  const selectedCentroid = useMemo(
    () => getGeojsonCentroid(selectedBoundary?.geojson),
    [selectedBoundary]
  );

  const headerCoords = useMemo(() => {
    return selectedCentroid ? formatCoords(selectedCentroid.lat, selectedCentroid.lng) : null;
  }, [selectedCentroid]);

  const loadBoundaries = useCallback(async () => {
    setLoadingBoundaries(true);
    setBoundariesError('');
    try {
      const res = await api.get('/hunt-boundaries/');
      const list = clubTracts(Array.isArray(res?.data) ? res.data : []);
      setBoundaries(list);
      if (list.length === 1) setHuntBoundaryId(String(list[0].id));
    } catch (err) {
      setBoundariesError('Unable to load your club boundaries.');
    } finally {
      setLoadingBoundaries(false);
    }
  }, []);

  useEffect(() => {
    loadBoundaries();
  }, [loadBoundaries]);

  const lastAutofilledBoundaryIdRef = useRef(null);
  useEffect(() => {
    if (!huntBoundaryId || boundaries.length === 0) return;
    if (lastAutofilledBoundaryIdRef.current === huntBoundaryId) return;
    lastAutofilledBoundaryIdRef.current = huntBoundaryId;
    const boundary = boundaries.find((b) => String(b.id) === huntBoundaryId);
    const club = boundary?.club_summary;
    const storedLogo = loadLogoChoice(huntBoundaryId);
    if (storedLogo) {
      setLogoUrl(storedLogo.url);
      setLogoPosition(storedLogo.position);
    } else {
      setLogoUrl(club?.logo_url || '');
      setLogoPosition(DEFAULT_LOGO_POSITION);
    }
    if (!club) return;
    const newTitle = club.established_year
      ? `${club.name} - Est. ${club.established_year}`
      : club.name;
    setTitle(newTitle);
  }, [huntBoundaryId, boundaries]);

  const applyLogoChoice = useCallback((url, position) => {
    const nextPosition = position === 'right' ? 'right' : 'left';
    setLogoUrl(url);
    setLogoPosition(nextPosition);
    if (huntBoundaryId) saveLogoChoice(huntBoundaryId, { url, position: nextPosition });
  }, [huntBoundaryId]);

  const handleLogoUpload = useCallback(
    async (e) => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      const allowed = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowed.includes(file.type)) {
        showToast('Please use JPEG, PNG, or WebP.', 'warning');
        return;
      }
      setLogoUploading(true);
      try {
        const uploadName = (file.name || 'logo.jpg').replace(/\.(heic|heif)$/i, '.jpg');
        const initRes = await api.post('/assets/init/', {
          filename: uploadName,
          content_type: file.type,
          scope: 'misc',
          public: true,
        });
        const { upload_url, object_key, reuse, asset: reused } = initRes.data || {};
        if (reuse && reused?.url) {
          applyLogoChoice(reused.url, logoPosition);
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
        await fetch(upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        const finalized = await assetsService.finalize({
          scope: 'misc',
          object_key,
        });
        const url = finalized?.url;
        if (url) applyLogoChoice(url, logoPosition);
        else showToast('Logo uploaded but URL missing.', 'warning');
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Upload failed.';
        showToast(msg, 'error');
      } finally {
        setLogoUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [showToast, applyLogoChoice, logoPosition]
  );

  const handleDownloadPdf = useCallback(async () => {
    if (!huntBoundaryId) {
      showToast('Please select a boundary.', 'warning');
      return;
    }
    setDownloadingPdf(true);
    try {
      const data = await mapPrintService.queuePdf({
        hunt_boundary_id: Number(huntBoundaryId),
        size,
        style,
        title: title.trim(),
        orientation,
        boundary_line_color: boundaryLineColor.trim() || undefined,
        boundary_fill_opacity: boundaryFillOpacity,
        boundary_line_scale: boundaryLineScale,
        ...(logoUrl.trim()
          ? { logo_url: logoUrl.trim(), logo_position: logoPosition }
          : {}),
      });
      showToast(
        data?.detail || "Your map is being prepared. We'll notify you when it's ready.",
        'success'
      );
    } catch (err) {
      const fallback = 'Could not queue the PDF. Please try again.';
      showToast(await errorDetailFromResponse(err, fallback), 'error');
    } finally {
      setDownloadingPdf(false);
    }
  }, [huntBoundaryId, size, style, title, orientation, boundaryLineColor, boundaryFillOpacity, boundaryLineScale, logoUrl, logoPosition, showToast]);

  const previewCaption = selectedBoundary
    ? `${selectedBoundary.name} · ${getSizeOption(size).label.replace(/\u2033/g, '"')}${headerCoords ? ` · ${headerCoords.split('|')[0].trim()}` : ''}`
    : null;

  const previewKey = [
    huntBoundaryId,
    size,
    title.trim(),
    previewCaption || '',
    orientation,
    boundaryLineColor,
    boundaryFillOpacity,
    boundaryLineScale,
    logoUrl,
    logoPosition,
  ].join('|');

  const handlePreviewRendered = useCallback((data) => {
    setPreviewSnapshot({ ...data, orientation, previewKey });
  }, [orientation, previewKey]);

  const previewReady = previewSnapshot?.previewKey === previewKey;

  const wizardState = useMemo(
    () => ({ huntBoundaryId, title, previewReady }),
    [huntBoundaryId, title, previewReady]
  );

  const continueEnabled = canContinueStep(step, wizardState);
  const isLastStep = step === WIZARD_STEPS.length;

  const handleContinue = () => {
    if (!continueEnabled || isLastStep) return;
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const renderStep1 = () => (
    <div>
      <StepHeading
        stepCode="Step 01 — Source"
        title="Choose a boundary"
        subtitle="Pick the club property to render. We'll fit the print to its surveyed boundary."
      />
      {loadingBoundaries ? (
        <Spinner animation="border" size="sm" />
      ) : boundariesError ? (
        <p className="text-danger mb-0">{boundariesError}</p>
      ) : boundaries.length === 0 ? (
        <p className="text-muted mb-0">You don't have any club boundaries yet. Create one from a club map first.</p>
      ) : (
        <div className="pm-boundary-list">
          {boundaries.map((boundary, index) => {
            const selected = String(boundary.id) === huntBoundaryId;
            return (
              <button
                key={boundary.id}
                type="button"
                className={`pm-boundary-card${selected ? ' is-selected' : ''}`}
                onClick={() => setHuntBoundaryId(String(boundary.id))}
                data-testid={`pm-boundary-${boundary.id}`}
              >
                <div className="pm-boundary-card__inner">
                  <div className="pm-boundary-card__thumb">
                    <BoundaryThumbnail geojson={boundary.geojson} patternId={`pm-grid-${boundary.id}`} />
                  </div>
                  <div className="pm-boundary-card__info">
                    <div className="pm-mono-label">{`Tract ${String(index + 1).padStart(2, '0')}`}</div>
                    <div className="pm-boundary-card__name">{boundary.name}</div>
                    {boundary.club_summary?.name ? (
                      <div className="pm-boundary-card__stat">{boundary.club_summary.name}</div>
                    ) : null}
                  </div>
                  <div className="pm-boundary-card__check">
                    <FiCheck aria-hidden="true" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="d-flex flex-column gap-4">
      <StepHeading
        stepCode="Step 02 — Configure"
        title="Map options"
        subtitle="Pick a print-shop size and name the sheet. The property boundary always prints."
      />

      <div>
        <div className="pm-section-title">Print size</div>
        <div className="pm-field-help mb-3">
          These match what retail shops stock. 18 × 24″ is a good default.
        </div>
        <div className="pm-tile-grid pm-tile-grid--4">
          {SIZES.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`pm-tile text-center${size === option.value ? ' is-selected' : ''}`}
              onClick={() => setSize(option.value)}
            >
              <div className="pm-size-label">{option.label}</div>
              <span className="pm-tile__check"><FiCheck aria-hidden="true" /></span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="pm-section-title">Map layers</div>
        <div className="pm-field-help mb-3">
          Stand markers and food plots are not on the sheet yet.
        </div>
        <YCard className="pm-layers-card">
          {LAYER_DEFS.map((layer) => {
            const Icon = LAYER_ICONS[layer.key];
            const checked = layers[layer.key];
            return (
              <div key={layer.key} className={`pm-layer-row${layer.stubbed ? ' is-stubbed' : ''}`}>
                <div className="d-flex align-items-center gap-2">
                  <span className="pm-layer-row__icon"><Icon aria-hidden="true" /></span>
                  <div>
                    <div className="pm-layer-row__label">{layer.label}</div>
                    <div className="pm-layer-row__desc">{layer.desc}</div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {layer.stubbed ? <span className="pm-mono-label">Soon</span> : null}
                  <button
                    type="button"
                    className={`pm-switch${checked ? ' is-checked' : ''}`}
                    role="switch"
                    aria-checked={checked}
                    aria-disabled={layer.stubbed ? 'true' : undefined}
                    disabled={layer.stubbed}
                    title={layer.stubbed ? 'Coming soon' : undefined}
                  >
                    <span className="pm-switch__thumb" />
                  </button>
                </div>
              </div>
            );
          })}
        </YCard>
      </div>

      <div className="pm-title-logo-grid">
        <div>
          <label className="y-label" htmlFor="pm-map-title">Map title</label>
          <YInput
            id="pm-map-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Deer Creek Hunt Club — Est. 2015"
            maxLength={120}
          />
          <div className="pm-field-help">Shown along the bottom of the print. Pre-filled from your club.</div>
        </div>
        <div>
          <span className="y-label d-block">Club logo</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleLogoUpload}
            style={{ display: 'none' }}
            disabled={logoUploading}
          />
          <div className="pm-logo-row">
            {logoUrl ? (
              <img src={logoUrl} alt="Club logo" className="pm-logo-thumb" />
            ) : null}
            <YButton
              type="button"
              variant="secondary"
              disabled={logoUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <FiUpload aria-hidden="true" />
              {logoUploading ? 'Uploading…' : logoUrl ? 'Replace' : 'Upload logo'}
            </YButton>
            {logoUrl ? (
              <YButton type="button" variant="ghost" onClick={() => applyLogoChoice('', logoPosition)}>
                Remove
              </YButton>
            ) : null}
          </div>
          {logoUrl ? (
            <div className="pm-logo-placement">
              <span className="y-label d-block">Logo placement</span>
              <div className="y-view-toggle" role="group" aria-label="Logo placement">
                {LOGO_POSITIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={logoPosition === option.value ? 'active' : ''}
                    aria-pressed={logoPosition === option.value}
                    data-testid={`pm-logo-position-${option.value}`}
                    onClick={() => applyLogoChoice(logoUrl, option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <div className="pm-field-help">
            Optional. Sits in the white title area below the map, not on the land.
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="d-flex flex-column gap-4">
      <div>
        <StepHeading
          stepCode="Step 03 — Layout"
          title="Preview the print"
          subtitle="This is rendered by the same engine that produces the print, so what you see is what you get."
        />
        <div className="pm-orientation-row">
          <div className="y-view-toggle" role="group" aria-label="Print orientation">
            {ORIENTATIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={orientation === option.value ? 'active' : ''}
                aria-pressed={orientation === option.value}
                data-testid={`pm-orientation-${option.value}`}
                onClick={() => setOrientation(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="pm-preview-tray">
        <ServerMapPrintPreview
          huntBoundaryId={huntBoundaryId}
          size={size}
          title={title}
          subtitle={previewCaption || ''}
          orientation={orientation}
          boundaryLineColor={boundaryLineColor}
          boundaryFillOpacity={boundaryFillOpacity}
          boundaryLineScale={boundaryLineScale}
          logoUrl={logoUrl}
          logoPosition={logoPosition}
          frameHeight={PREVIEW_FRAME_HEIGHT}
          onRendered={handlePreviewRendered}
        />
      </div>
      <div className="pm-preview-controls">
        <YCard bodyClassName="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="y-label mb-0" htmlFor="pm-fill-opacity">Boundary fill</label>
            <span className="pm-mono-label">{Math.round(boundaryFillOpacity * 100)}%</span>
          </div>
          <input
            id="pm-fill-opacity"
            className="pm-range"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={boundaryFillOpacity}
            onChange={(e) => setBoundaryFillOpacity(Number(e.target.value))}
          />
          <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
            <label className="y-label mb-0" htmlFor="pm-line-scale">Boundary thickness</label>
            <span className="pm-mono-label">{Math.round(boundaryLineScale * 100)}%</span>
          </div>
          <input
            id="pm-line-scale"
            className="pm-range"
            type="range"
            min={BOUNDARY_LINE_SCALE_MIN}
            max={BOUNDARY_LINE_SCALE_MAX}
            step={0.25}
            value={boundaryLineScale}
            onChange={(e) => setBoundaryLineScale(Number(e.target.value))}
          />
        </YCard>
        <YCard bodyClassName="p-3">
          <span className="y-label d-block">Outline color</span>
          <div className="pm-swatch-row">
            {OUTLINE_SWATCHES.map((swatch) => (
              <button
                key={swatch.value}
                type="button"
                className={`pm-color-swatch${boundaryLineColor.toLowerCase() === swatch.value.toLowerCase() ? ' is-selected' : ''}${swatch.value.toLowerCase() === '#ffffff' ? ' is-light' : ''}`}
                style={{ background: swatch.value }}
                aria-label={swatch.label}
                onClick={() => setBoundaryLineColor(swatch.value)}
              />
            ))}
          </div>
        </YCard>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="d-flex flex-column gap-4">
      <StepHeading
        stepCode="Step 04 — Print"
        title="Review & print"
        subtitle="This is the sheet you approved. Download the PDF and take it to a print shop."
      />
      <div className="pm-review-print">
        <div className="pm-preview-tray">
          <PrintableMapFrozenPreview
            snapshot={previewSnapshot}
            title={title}
            size={size}
            frameHeight={PREVIEW_FRAME_HEIGHT}
          />
        </div>
        <PrintableMapPrintGuidance landCentroid={selectedCentroid} />
      </div>
    </div>
  );

  return (
    <YPageShell className="pm-page pm-page--field" data-testid="printable-map-page">
      <ToolkitShellTabs />
      <header className="pm-header pm-header--field">
        <div className="pm-header__brand">
          <p className="pm-header__eyebrow">PRINTABLE MAP</p>
          <h1 className="pm-header__title">Print a club map</h1>
        </div>
        <div className="pm-header__meta">
          {headerCoords ? <span className="pm-header__coords">{headerCoords}</span> : null}
          <span className="pm-status-tag">
            <span className="pm-status-tag__dot" />
            Print-ready PDF
          </span>
        </div>
      </header>

      <YPageShellBody className="pm-body">
        <div className="pm-wizard" data-testid="pm-wizard">
          <PrintableMapStepper step={step} />
          <div className={`pm-grid${isLastStep ? ' pm-grid--single' : ''}`}>
            <div className="pm-panel">
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}

              <div className="pm-action-bar">
                {step > 1 ? (
                  <YButton type="button" variant="ghost" size="lg" onClick={handleBack}>
                    <FiArrowLeft aria-hidden="true" />
                    Back
                  </YButton>
                ) : null}
                <div className="pm-action-bar__spacer" />
                <span className="pm-action-bar__counter">{`Step ${step} / ${WIZARD_STEPS.length}`}</span>
                {isLastStep ? (
                  <YButton
                    type="button"
                    variant="primary"
                    size="lg"
                    disabled={!huntBoundaryId || downloadingPdf}
                    onClick={handleDownloadPdf}
                    data-testid="pm-download-pdf-btn"
                  >
                    {downloadingPdf ? <Spinner animation="border" size="sm" /> : <FiDownload aria-hidden="true" />}
                    {downloadingPdf ? 'Requesting…' : 'Download PDF'}
                  </YButton>
                ) : (
                  <YButton
                    type="button"
                    variant="primary"
                    size="lg"
                    disabled={!continueEnabled}
                    onClick={handleContinue}
                    data-testid="pm-continue-btn"
                  >
                    Continue
                    <FiArrowRight aria-hidden="true" />
                  </YButton>
                )}
              </div>
            </div>

            {!isLastStep ? (
              <PrintableMapOrderSummary
                boundaryName={selectedBoundary?.name}
                size={size}
                orientation={orientation}
                title={title}
              />
            ) : null}
          </div>
        </div>
      </YPageShellBody>
    </YPageShell>
  );
}
